import ActionDialog from "@dashboard/components/ActionDialog";
import { TopNav } from "@dashboard/components/AppLayout/TopNav";
import { DetailPageLayout } from "@dashboard/components/Layouts";
import NotFoundPage from "@dashboard/components/NotFoundPage";
import {
  useWsmFitmentQualifierDeleteMutation,
  useWsmFitmentQualifierUpdateMutation,
  useWsmFitmentQualifierValueBulkCreateMutation,
  useWsmFitmentQualifierValueDeleteMutation,
  useWsmFitmentQualifierValuesQuery,
  useWsmFitmentQualifierValueUpdateMutation,
  WsmErrorFragment,
  WsmFitmentValueKind,
} from "@dashboard/graphql";
import useNavigator from "@dashboard/hooks/useNavigator";
import useNotifier from "@dashboard/hooks/useNotifier";
import { commonMessages } from "@dashboard/intl";
import { mapEdgesToItems } from "@dashboard/utils/maps";
import { Skeleton, Text } from "@saleor/macaw-ui-next";
import React, { useRef, useState } from "react";
import { useIntl } from "react-intl";

import { removeFromWsmFitmentList } from "../cache";
import { WsmFitmentLoadError } from "../components/WsmFitmentLoadError";
import WsmFitmentNamedDetailsPage from "../components/WsmFitmentNamedDetailsPage";
import WsmFitmentValuesCard from "../components/WsmFitmentValuesCard";
import { getWsmFitmentErrorMessage } from "../errors";
import { useWsmFitmentAttempt } from "../hooks/useWsmFitmentAttempt";
import {
  useWsmFitmentEmptyPageFallback,
  useWsmFitmentPagedList,
} from "../hooks/useWsmFitmentPagedValues";
import { useWsmFitmentQualifiers } from "../hooks/useWsmFitmentVocabulary";
import { qualifierMessages, sectionMessages, valueMessages } from "../messages";
import { wsmFitmentQualifierListPath } from "../urls";
import { getWriteState, toWriteResult } from "../utils";

// The nested `values` on the qualifier list feed the application picker, so a
// value write refetches both the paged connection and the list.
const VALUE_QUERIES = ["WsmFitmentQualifierValues", "WsmFitmentQualifiers"];

export const WsmFitmentQualifierDetailsView = ({ id }: { id: string }) => {
  const intl = useIntl();
  const navigate = useNavigator();
  const notify = useNotifier();
  const [deleteOpen, setDeleteOpen] = useState(false);
  // Shown on the delete dialog: this call's result only, cleared on open.
  const [deleteErrors, setDeleteErrors] = useState<WsmErrorFragment[]>([]);
  const deleteAttempt = useWsmFitmentAttempt();
  const { qualifiers, loading, error: listError, retry } = useWsmFitmentQualifiers();
  const { variables, bind, resetPage } = useWsmFitmentPagedList();
  const { data: valuesData, loading: valuesLoading } = useWsmFitmentQualifierValuesQuery({
    variables: { qualifier: id, ...variables },
  });
  const valueRows = valuesLoading
    ? undefined
    : (mapEdgesToItems(valuesData?.wsmFitmentQualifierValues) ?? []);

  // Deleting the last row of the last page must not strand the table there.
  useWsmFitmentEmptyPageFallback(variables, valueRows, resetPage);

  // Payload errors render on the form and the dialogs; a toast would say it twice.
  // Qualifier values take no `search` on the server, so the card has no search box.
  const [updateQualifier, updateQualifierOpts] = useWsmFitmentQualifierUpdateMutation({
    disableNestedErrorHandling: true,
    onCompleted: result => {
      if (!result.wsmFitmentQualifierUpdate?.errors.length) {
        notify({ status: "success", text: intl.formatMessage(commonMessages.savedChanges) });
      }
    },
  });
  const [deleteQualifier] = useWsmFitmentQualifierDeleteMutation({
    disableNestedErrorHandling: true,
    // The row leaves the cached list here, so the list is right the moment it
    // mounts; the awaited refetch then lands while this screen still holds the
    // query, instead of being cancelled by the navigation.
    update: (cache, { data }) => {
      if (data?.wsmFitmentQualifierDelete && !data.wsmFitmentQualifierDelete.errors.length) {
        removeFromWsmFitmentList(cache, "wsmFitmentQualifiers", {
          __typename: "WsmFitmentQualifier",
          id,
        });
      }
    },
    refetchQueries: ["WsmFitmentQualifiers"],
    awaitRefetchQueries: true,
    onCompleted: result => {
      if (result.wsmFitmentQualifierDelete && !result.wsmFitmentQualifierDelete.errors.length) {
        notify({ status: "success", text: intl.formatMessage(commonMessages.savedChanges) });
        navigate(wsmFitmentQualifierListPath);
      }
    },
  });
  const [addValues, addValuesOpts] = useWsmFitmentQualifierValueBulkCreateMutation({
    disableNestedErrorHandling: true,
    refetchQueries: VALUE_QUERIES,
  });
  const [renameValue, renameValueOpts] = useWsmFitmentQualifierValueUpdateMutation({
    disableNestedErrorHandling: true,
  });
  const [deleteValue, deleteValueOpts] = useWsmFitmentQualifierValueDeleteMutation({
    disableNestedErrorHandling: true,
    refetchQueries: VALUE_QUERIES,
  });
  const found = qualifiers.find(candidate => candidate.id === id);
  // This screen's own delete takes the row out of the cache before it navigates
  // away; until then it keeps showing the last copy rather than "Not found".
  const lastSeen = useRef(found);

  if (found) {
    lastSeen.current = found;
  }

  const deleting = deleteAttempt.state === "loading" || deleteAttempt.state === "success";
  const qualifier = found ?? (deleting ? lastSeen.current : undefined);

  // A list that failed to load says so, rather than calling the row missing.
  if ((loading || listError) && !qualifier) {
    return (
      <DetailPageLayout>
        <TopNav
          href={wsmFitmentQualifierListPath}
          title={intl.formatMessage(sectionMessages.qualifiers)}
        />
        <DetailPageLayout.Content>
          {listError ? <WsmFitmentLoadError onRetry={retry} /> : <Skeleton />}
        </DetailPageLayout.Content>
      </DetailPageLayout>
    );
  }

  if (!qualifier) {
    return <NotFoundPage backHref={wsmFitmentQualifierListPath} />;
  }

  const updatePayload = updateQualifierOpts.data?.wsmFitmentQualifierUpdate;
  const errors = updatePayload?.errors ?? [];
  const valuesBusy = addValuesOpts.loading || renameValueOpts.loading || deleteValueOpts.loading;

  return (
    <>
      <WsmFitmentNamedDetailsPage
        key={qualifier.id}
        title={qualifier.name}
        backHref={wsmFitmentQualifierListPath}
        withValueKind={false}
        initialData={{
          name: qualifier.name,
          displayLabel: qualifier.displayLabel,
          valueKind: WsmFitmentValueKind.TEXT,
        }}
        errors={errors}
        disabled={updateQualifierOpts.loading}
        saveButtonBarState={getWriteState(updateQualifierOpts, updatePayload)}
        onSubmit={data =>
          updateQualifier({
            variables: {
              id: qualifier.id,
              input: { name: data.name.trim(), displayLabel: data.displayLabel.trim() },
            },
          })
        }
        onDelete={() => {
          setDeleteErrors([]);
          deleteAttempt.reset();
          setDeleteOpen(true);
        }}
      >
        <WsmFitmentValuesCard
          title={intl.formatMessage(qualifierMessages.valuesTitle)}
          values={valueRows}
          totalCount={valuesData?.wsmFitmentQualifierValues?.totalCount}
          paging={bind(valuesData?.wsmFitmentQualifierValues?.pageInfo)}
          disabled={valuesBusy}
          deleteMessage={valueMessages.qualifierValueDeleteContent}
          onAdd={async values => {
            const result = await addValues({ variables: { qualifier: qualifier.id, values } });
            const payload = result.data?.wsmFitmentQualifierValueBulkCreate;

            if (payload && !payload.errors.length) {
              notify({
                status: "success",
                text: intl.formatMessage(valueMessages.addCount, { count: payload.count }),
              });
            }

            return toWriteResult(payload);
          }}
          onRename={async (valueId, value) => {
            const result = await renameValue({ variables: { id: valueId, input: { value } } });

            return toWriteResult(result.data?.wsmFitmentQualifierValueUpdate);
          }}
          onDelete={async valueId => {
            const result = await deleteValue({ variables: { id: valueId } });

            return toWriteResult(result.data?.wsmFitmentQualifierValueDelete);
          }}
        />
      </WsmFitmentNamedDetailsPage>
      <ActionDialog
        open={deleteOpen}
        variant="delete"
        title={intl.formatMessage(qualifierMessages.deleteTitle)}
        confirmButtonState={deleteAttempt.state}
        onClose={() => setDeleteOpen(false)}
        onConfirm={async () => {
          const result = await deleteAttempt.run(async () =>
            toWriteResult(
              (await deleteQualifier({ variables: { id: qualifier.id } })).data
                ?.wsmFitmentQualifierDelete,
            ),
          );

          setDeleteErrors(result.errors);
        }}
      >
        <Text display="block">
          {intl.formatMessage(qualifierMessages.deleteContent, { name: qualifier.name })}
        </Text>
        {deleteErrors[0] && (
          <Text
            display="block"
            color="critical1"
            marginTop={2}
            data-test-id="wsm-fitment-qualifier-delete-error"
          >
            {getWsmFitmentErrorMessage(deleteErrors[0], intl)}
          </Text>
        )}
      </ActionDialog>
    </>
  );
};

export default WsmFitmentQualifierDetailsView;
